import { createStyles, Tabs } from "@mantine/core";
import { tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { IconV3 } from "@rpi/openapi-core";
import React from "react";
import { useParams } from "react-router-dom";
import { Body, Heading } from "../../../components/Typography";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { RouteLink } from "../../../hooks/useNavigateRoute";
import { RpiCard } from "../../../rpi-core/card/RpiCard";
import { PaymentDetailsTab } from "./PaymentDetailsTab";
import { PaymentOrdersTab } from "./PaymentOrdersTab";

const PAYMENT_TABS = [
  { label: "Details", value: "details" },
  { label: "Orders", value: "orders" },
] as const;

type PaymentTabValue = typeof PAYMENT_TABS[number]["value"];

function isPaymentTabValue(value: string | null): value is PaymentTabValue {
  return PAYMENT_TABS.some((tab) => tab.value === value);
}

const useStyles = createStyles(() => ({
  container: {
    height: "100%",
    overflowY: "auto" as const,
    padding: tokens.spacing[20],
    display: "flex",
    flexDirection: "column" as const,
    gap: tokens.spacing[6],
    alignItems: "flex-start",
  },
  backLink: {
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.spacing[2],
  },
  card: {
    display: "flex",
    flexDirection: "column" as const,
    padding: tokens.spacing[6],
    gap: tokens.spacing[6],
    alignSelf: "stretch",
  },
  headerGroup: {
    display: "flex",
    flexDirection: "column" as const,
    gap: tokens.spacing[2],
  },
  tabsContainer: {
    display: "flex",
    flexDirection: "column" as const,
  },
  panel: {
    paddingTop: tokens.spacing[6],
  },
}));

function isSuperAdminWithBraintreeId(
  isSuperAdmin: boolean,
  braintreeTransactionId: string | null | undefined
): braintreeTransactionId is string {
  return isSuperAdmin && !!braintreeTransactionId;
}

function parsePaymentId(raw: string | undefined): number | null {
  if (!raw || !/^\d+$/.test(raw)) return null;
  return Number(raw);
}

export const PaymentViewV2: React.FC = () => {
  const { id } = useParams();
  const user = useAuthUser();
  const { classes } = useStyles();
  const [activeTab, setActiveTab] = React.useState<PaymentTabValue>("details");

  const paymentId = parsePaymentId(id);
  const { data, isLoading } = GraphQL.usePaymentQuery({ id: paymentId ?? 0 }, { enabled: paymentId !== null });
  const braintreeTransactionId = data?.payment.braintreeTransactionId;

  return (
    <div className={classes.container}>
      <RouteLink route={["billing"]} className={classes.backLink}>
        <IconV3 icon="chevron_left" size={20} />
        <Body weight="semibold">Back to All Payments</Body>
      </RouteLink>

      <RpiCard className={classes.card}>
        <div className={classes.headerGroup}>
          <Heading size="xl" weight="bold">
            {id}
          </Heading>
          {isSuperAdminWithBraintreeId(user.is.SUPER_ADMIN, braintreeTransactionId) && (
            <Body>Braintree ID# {braintreeTransactionId}</Body>
          )}
        </div>

        <Tabs
          value={activeTab}
          onTabChange={(value) => {
            if (isPaymentTabValue(value)) setActiveTab(value);
          }}
          className={classes.tabsContainer}
        >
          <Tabs.List>
            {PAYMENT_TABS.map((tab) => (
              <Tabs.Tab key={tab.value} value={tab.value}>
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          <Tabs.Panel value="details" className={classes.panel}>
            <PaymentDetailsTab payment={data?.payment} isLoading={isLoading} />
          </Tabs.Panel>
          <Tabs.Panel value="orders" className={classes.panel}>
            <PaymentOrdersTab payment={data?.payment} isLoading={isLoading} />
          </Tabs.Panel>
        </Tabs>
      </RpiCard>
    </div>
  );
};
